import { useRouterState } from '@tanstack/react-router';
import { contentFor, type ContentBundle } from '~/content/localized';
import {
  htmlDirForLanguage,
  languageFromPathname,
  stripLanguagePrefix,
  translatedPathFor,
  type Language,
} from './language';

/**
 * Current language, derived from the URL.
 *
 * The pathname is the single source of truth — no context provider and no client
 * state — so server render, hydration and back/forward navigation can never
 * disagree about which language the page is in.
 */
export function useLanguage(): Language {
  return useRouterState({ select: ({ location }) => languageFromPathname(location.pathname) });
}

/** Content bundle for the current language. */
export function useContent(): ContentBundle {
  return contentFor(useLanguage());
}

/** Text direction for the current language. */
export function useDirection(): 'ltr' | 'rtl' {
  return htmlDirForLanguage(useLanguage());
}

/**
 * Resolve an internal href for the current language.
 *
 * Only paths that actually have a translated route get the language prefix;
 * everything else falls back to the canonical English URL. That fallback is the
 * point: during a page-by-page rollout, prefixing a path whose translation does
 * not exist yet would produce a 404. A visitor browsing Arabic therefore lands on
 * the English version of a not-yet-translated page rather than a dead end.
 *
 * External hrefs, mailto: and in-page anchors pass through untouched.
 */
export function useLocalizedPath(): (path: string) => string {
  const language = useLanguage();
  return (path: string) => {
    if (/^(?:[a-z]+:)?\/\//iu.test(path) || path.startsWith('mailto:') || path.startsWith('#')) {
      return path;
    }
    return translatedPathFor(path, language) ?? stripLanguagePrefix(path);
  };
}
